import React, { useEffect, useState, useCallback } from "react";
import { Button, Table, Tag, Modal, message, Input, Space, Card, Statistic } from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  UserOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined
} from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import {
  getDriverPenaltyStatusApi,
  removeExpiredPenaltiesApi,
  removeDriverPenaltyApi,
  getAllPenalizedDriversApi,
  searchPenalizedDriversApi,
  runAntiIdleAuditApi
} from "api/penalty";
import { useNotificationContext } from "contexts/NotificationContext";
import { IPenaltyStatus } from "models";

const { confirm } = Modal;

export default function PenaltyManagement() {
  const { showError } = useNotificationContext();
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [phoneSearch, setPhoneSearch] = useState("");
  const [penalizedDrivers, setPenalizedDrivers] = useState<IPenaltyStatus[]>([]);
  const [stats, setStats] = useState({
    totalPenalized: 0,
    expiredCount: 0,
    activeCount: 0
  });

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  const fetchPenalizedDrivers = useCallback(async (page: number = pagination.current, pageSize: number = pagination.pageSize) => {
    setLoading(true);
    try {
      // Use search API if phone or name search is active
      const { data: response, error } = (phoneSearch || searchText)
        ? await searchPenalizedDriversApi(page, pageSize, phoneSearch, searchText)
        : await getAllPenalizedDriversApi(page, pageSize);

      if (response) {
        setPenalizedDrivers(response.data);
        setPagination(prev => ({ ...prev, current: page, pageSize, total: response.total }));

        // Calculate statistics from data
        const total = response.data.length;
        const active = response.data.filter(d => d.is_penalized).length;
        const expired = total - active;

        setStats({
          totalPenalized: response.total,
          expiredCount: expired,
          activeCount: active
        });
      } else {
        showError(error?.message || "Failed to fetch penalized drivers");
        setPenalizedDrivers([]);
      }
    } catch (error) {
      showError("Failed to fetch penalized drivers");
      setPenalizedDrivers([]);
    } finally {
      setLoading(false);
    }
  }, [showError, phoneSearch, searchText, pagination.current, pagination.pageSize]);

  const handleTableChange = (pagination: any) => {
    fetchPenalizedDrivers(pagination.current, pagination.pageSize);
  };

  const handleRemoveExpiredPenalties = async () => {
    confirm({
      title: "Remove Expired Penalties",
      icon: <ExclamationCircleOutlined />,
      content: "This will remove all penalties that have expired. Continue?",
      onOk: async () => {
        try {
          const { data, error } = await removeExpiredPenaltiesApi();
          if (data) {
            message.success(`Removed ${data.removed_count} expired penalties`);
            fetchPenalizedDrivers();
          } else {
            showError(error?.message || "Failed to remove expired penalties");
          }
        } catch (err) {
          showError("Failed to remove expired penalties");
        }
      },
    });
  };

  const handleRemoveDriverPenalty = async (driverId: string) => {
    confirm({
      title: "Remove Driver Penalty",
      icon: <ExclamationCircleOutlined />,
      content: "This will manually remove the penalty from this driver. Continue?",
      onOk: async () => {
        try {
          const { data, error } = await removeDriverPenaltyApi(driverId);
          if (data) {
            message.success("Penalty removed successfully");
            fetchPenalizedDrivers();
          } else {
            showError(error?.message || "Failed to remove penalty");
          }
        } catch (err) {
          showError("Failed to remove penalty");
        }
      },
    });
  };

  const handleCheckDriverPenalty = async (driverId: string) => {
    try {
      const { data, error } = await getDriverPenaltyStatusApi(driverId);
      if (data) {
        Modal.info({
          title: "Driver Penalty Status",
          content: (
            <div>
              <p><strong>Driver ID:</strong> {data.driver_id}</p>
              <p><strong>Penalized:</strong> {data.is_penalized ? "Yes" : "No"}</p>
              <p><strong>Daily Declines:</strong> {data.daily_decline_count}/5</p>
              <p><strong>Total Penalties:</strong> {data.total_penalties}</p>
              {data.penalty_expiry && (
                <p><strong>Expires:</strong> {new Date(data.penalty_expiry).toLocaleString()}</p>
              )}
              {data.penalty_reason && (
                <p><strong>Reason:</strong> {data.penalty_reason}</p>
              )}
            </div>
          ),
        });
      } else {
        showError(error?.message || "Failed to check driver penalty status");
      }
    } catch (err) {
      showError("Failed to check driver penalty status");
    }
  };

  const handleRunAntiIdleAudit = async () => {
    confirm({
      title: "Run System Anti-Idle Audit",
      icon: <ExclamationCircleOutlined />,
      content: "This will identify drivers who were active today but didn't perform any trips and apply the configured penalty. Continue?",
      onOk: async () => {
        setLoading(true);
        try {
          const { data, error } = await runAntiIdleAuditApi();
          if (data) {
            message.info(data.message);
            // Since it's background, we just refresh after a second
            setTimeout(fetchPenalizedDrivers, 1500);
          } else {
            showError(error?.message || "Failed to trigger audit");
          }
        } catch (err) {
          showError("Failed to trigger audit");
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const columns: ColumnsType<IPenaltyStatus> = [
    {
      title: "Driver ID",
      dataIndex: "driver_id",
      key: "driver_id",
      render: (id: string) => (
        <Button
          type="link"
          onClick={() => handleCheckDriverPenalty(id)}
          style={{ padding: 0 }}
        >
          {id}
        </Button>
      ),
    },
    {
      title: "Name",
      key: "name",
      render: (_, record) => `${record.first_name || ""} ${record.last_name || ""}`.trim() || "-",
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (phone: string) => phone || "-",
    },
    {
      title: "Status",
      dataIndex: "is_penalized",
      key: "is_penalized",
      render: (isPenalized: boolean) => (
        <Tag color={isPenalized ? "red" : "green"}>
          {isPenalized ? "Penalized" : "Active"}
        </Tag>
      ),
    },
    {
      title: "Daily Declines",
      dataIndex: "daily_decline_count",
      key: "daily_decline_count",
      render: (count: number) => `${count}`,
    },
    {
      title: "Total Penalties",
      dataIndex: "total_penalties",
      key: "total_penalties",
    },
    {
      title: "Penalty Expiry",
      dataIndex: "penalty_expiry",
      key: "penalty_expiry",
      render: (expiry: string) => expiry ? new Date(expiry).toLocaleString() : "-",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            onClick={() => handleCheckDriverPenalty(record.driver_id)}
            icon={<UserOutlined />}
          >
            Check Status
          </Button>
          {record.is_penalized && (
            <Button
              danger
              size="small"
              onClick={() => handleRemoveDriverPenalty(record.driver_id)}
            >
              Remove Penalty
            </Button>
          )}
        </Space>
      ),
    },
  ];

  useEffect(() => {
    fetchPenalizedDrivers();
  }, [fetchPenalizedDrivers]);

  return (
    <div className="w-full p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Driver Penalty Management</h1>
        <Space>
          <Button
            danger
            icon={<ExclamationCircleOutlined />}
            onClick={handleRunAntiIdleAudit}
            loading={loading}
          >
            Run Anti-Idle Audit
          </Button>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={handleRemoveExpiredPenalties}
          >
            Remove Expired Penalties
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => fetchPenalizedDrivers()}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <Statistic
            title="Total Penalized Drivers"
            value={stats.totalPenalized}
            prefix={<ExclamationCircleOutlined />}
            valueStyle={{ color: '#cf1322' }}
          />
        </Card>
        <Card>
          <Statistic
            title="Active Penalties"
            value={stats.activeCount}
            prefix={<ClockCircleOutlined />}
            valueStyle={{ color: '#faad14' }}
          />
        </Card>
        <Card>
          <Statistic
            title="Expired Penalties"
            value={stats.expiredCount}
            prefix={<CheckCircleOutlined />}
            valueStyle={{ color: '#52c41a' }}
          />
        </Card>
      </div>

      <Card>
        <div className="mb-4">
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Space>
              <Input
                placeholder="Search by Phone Number (e.g., 251911111111)"
                prefix={<SearchOutlined />}
                value={phoneSearch}
                onChange={(e) => setPhoneSearch(e.target.value)}
                style={{ width: 350 }}
                allowClear
              />
              <Input
                placeholder="Search by Name"
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 300 }}
                allowClear
              />
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={() => fetchPenalizedDrivers(1)}
                loading={loading}
              >
                Search
              </Button>
              {(phoneSearch || searchText) && (
                <Button
                  onClick={() => {
                    setPhoneSearch("");
                    setSearchText("");
                  }}
                >
                  Clear
                </Button>
              )}
            </Space>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={penalizedDrivers}
          loading={loading}
          rowKey="driver_id"
          onChange={handleTableChange}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} drivers`,
          }}
        />
      </Card>
    </div>
  );
}
